import { useState } from "react";
import { Link } from "react-router-dom";
import { useReviews } from "../../../hooks/useReviews";
import { useUser } from "../../../contexts/UserContext";
import { calcDistance } from "../../../utils/distance";
import { useGeolocation } from "../../../hooks/useGeolocation";
import type { Review } from "../../../hooks/useReviews";

interface Props {
  venueId: string;
  venueName: string;
  venueLat: number;
  venueLng: number;
}

const LOCATION_GATE_METERS = 100;

// ── Star Rating ───────────────────────────────────────────────────────────────
function StarRating({
  value,
  onChange,
  size = "md",
}: {
  value: number;
  onChange?: (v: number) => void;
  size?: "sm" | "md" | "lg";
}) {
  const [hovered, setHovered] = useState(0);
  const sz = size === "lg" ? "text-2xl" : size === "md" ? "text-xl" : "text-base";
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange?.(s)}
          onMouseEnter={() => onChange && setHovered(s)}
          onMouseLeave={() => onChange && setHovered(0)}
          className={`${onChange ? "cursor-pointer" : "cursor-default"} transition-transform ${onChange && "hover:scale-110 active:scale-95"}`}
        >
          <i
            className={`${sz} ${
              (hovered || value) >= s ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// ── Reviewer Avatar ───────────────────────────────────────────────────────────
function ReviewerAvatar({ review }: { review: Review }) {
  const name = review.displayName || review.username;
  const initials = name.charAt(0).toUpperCase();
  const colors = [
    "from-emerald-400 to-emerald-700",
    "from-amber-400 to-orange-600",
    "from-rose-400 to-pink-700",
    "from-sky-400 to-indigo-600",
    "from-violet-400 to-purple-700",
  ];
  const colorIdx = name.charCodeAt(0) % colors.length;

  if (review.avatarUrl) {
    return (
      <img
        src={review.avatarUrl}
        alt={name}
        className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0 border border-stone-100"
      />
    );
  }

  return (
    <div
      className={`w-10 h-10 rounded-full bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center flex-shrink-0`}
    >
      <span className="text-white font-bold text-sm">{initials}</span>
    </div>
  );
}

// ── Helpful Button ────────────────────────────────────────────────────────────
function HelpfulButton({
  review,
  voted,
  loggedIn,
  onToggle,
}: {
  review: Review;
  voted: boolean;
  loggedIn: boolean;
  onToggle: () => void;
}) {
  const [animating, setAnimating] = useState(false);

  const handleClick = () => {
    if (!loggedIn) return;
    setAnimating(true);
    setTimeout(() => setAnimating(false), 400);
    onToggle();
  };

  return (
    <button
      onClick={handleClick}
      title={loggedIn ? (voted ? "Oyunuzu geri alın" : "Faydalı buldunuz mu?") : "Giriş yaparak oy verin"}
      className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 cursor-pointer ${
        voted
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
          : "bg-stone-50 text-stone-400 border border-stone-200 hover:border-stone-300 hover:text-stone-600"
      } ${!loggedIn ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <i
        className={`${voted ? "ri-thumb-up-fill" : "ri-thumb-up-line"} transition-transform duration-200 ${
          animating ? "scale-150" : "scale-100"
        } ${voted ? "text-emerald-600" : ""}`}
      />
      <span>Faydalı</span>
      {review.helpful > 0 && (
        <span
          className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
            voted ? "bg-emerald-100 text-emerald-700" : "bg-stone-200 text-stone-600"
          }`}
        >
          {review.helpful}
        </span>
      )}
    </button>
  );
}

// ── Review Card ───────────────────────────────────────────────────────────────
function ReviewCard({
  review,
  voted,
  loggedIn,
  onToggleHelpful,
}: {
  review: Review;
  voted: boolean;
  loggedIn: boolean;
  onToggleHelpful: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const name = review.displayName || review.username;
  const isLong = review.comment.length > 200;
  const displayText = isLong && !expanded ? review.comment.slice(0, 200) + "…" : review.comment;

  const formattedDate = new Date(review.date).toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="bg-white border border-stone-100 rounded-2xl p-5 transition-all duration-200 hover:border-stone-200">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <Link to={`/profile/${review.username}`} className="flex-shrink-0 cursor-pointer">
          <ReviewerAvatar review={review} />
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-1.5 mb-0.5">
            <Link
              to={`/profile/${review.username}`}
              className="font-semibold text-stone-900 text-sm hover:text-emerald-700 transition-colors cursor-pointer"
            >
              {name}
            </Link>
            <span className="text-stone-300 text-xs">·</span>
            <span className="text-stone-400 text-xs">@{review.username}</span>
            {review.verified && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                <i className="ri-shield-check-fill text-xs" />
                Doğrulanmış
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-0.5">
            <StarRating value={review.rating} size="sm" />
            <span className="text-xs font-semibold text-stone-700">{review.rating}.0</span>
            <span className="text-stone-300 text-xs">·</span>
            <span className="text-xs text-stone-400">{formattedDate}</span>
          </div>
        </div>

        {/* Rating pill (right) */}
        <div
          className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${
            review.rating >= 4
              ? "bg-emerald-50 text-emerald-700"
              : review.rating === 3
              ? "bg-amber-50 text-amber-700"
              : "bg-red-50 text-red-600"
          }`}
        >
          {review.rating}.0
        </div>
      </div>

      {/* Comment */}
      <div className="mb-3 pl-13" style={{ paddingLeft: "52px" }}>
        <p className="text-stone-600 text-sm leading-relaxed">
          {displayText}
        </p>
        {isLong && (
          <button
            onClick={() => setExpanded((p) => !p)}
            className="text-xs text-emerald-600 hover:text-emerald-800 font-medium mt-1 cursor-pointer whitespace-nowrap"
          >
            {expanded ? "Daha az göster" : "Devamını oku"}
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pl-13" style={{ paddingLeft: "52px" }}>
        <HelpfulButton
          review={review}
          voted={voted}
          loggedIn={loggedIn}
          onToggle={() => onToggleHelpful(review.id)}
        />
        {!loggedIn && (
          <Link
            to="/account"
            className="text-xs text-stone-400 hover:text-emerald-600 transition-colors cursor-pointer"
          >
            Oy vermek için giriş yap
          </Link>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ReviewSection({ venueId, venueName, venueLat, venueLng }: Props) {
  const { user } = useUser();
  const { reviews, averageRating, addReview, hasUserReviewed, toggleHelpful, myVotes, loading } = useReviews(venueId);
  const geo = useGeolocation();

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [submitMsg, setSubmitMsg] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState<"recent" | "helpful" | "rating">("recent");

  const isNearVenue =
    geo.status === "success" &&
    geo.lat !== null &&
    geo.lng !== null &&
    calcDistance(geo.lat, geo.lng, venueLat, venueLng) <= LOCATION_GATE_METERS;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (rating === 0) { setSubmitState("error"); setSubmitMsg("Lütfen bir yıldız puanı seçin."); return; }
    if (comment.length < 10) { setSubmitState("error"); setSubmitMsg("Yorum en az 10 karakter olmalıdır."); return; }

    setSubmitState("submitting");
    const result = await addReview(user.id, user.username || user.displayName, rating, comment);
    if (result.success) {
      setSubmitState("success");
      setSubmitMsg("Yorumunuz başarıyla eklendi! Teşekkürler.");
      setRating(0);
      setComment("");
      setShowForm(false);
    } else {
      setSubmitState("error");
      setSubmitMsg(result.error ?? "Yorum eklenemedi.");
    }
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    if (sortBy === "helpful") return b.helpful - a.helpful;
    if (sortBy === "rating") return b.rating - a.rating;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const alreadyReviewed = user ? hasUserReviewed(user.id) : false;

  const ratingDist = [5, 4, 3, 2, 1].map((s) => ({
    star: s,
    count: reviews.filter((r) => r.rating === s).length,
    pct: reviews.length > 0 ? (reviews.filter((r) => r.rating === s).length / reviews.length) * 100 : 0,
  }));

  return (
    <section className="mb-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-stone-800">Yorumlar & Puanlama</h2>
        {reviews.length > 0 && (
          <div className="flex items-center gap-1 bg-amber-50 border border-amber-100 rounded-xl px-3 py-1.5">
            <i className="ri-star-fill text-amber-500 text-sm" />
            <span className="font-bold text-stone-800 text-sm">{averageRating.toFixed(1)}</span>
            <span className="text-stone-400 text-xs">/ 5.0</span>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-5 mb-8 p-5 bg-stone-50 rounded-2xl">
        {/* Overall score */}
        <div className="md:col-span-2 flex items-center justify-center gap-5">
          <div className="text-center">
            <div className="text-6xl font-bold text-stone-900 leading-none">
              {averageRating > 0 ? averageRating.toFixed(1) : "—"}
            </div>
            <div className="mt-2">
              <StarRating value={Math.round(averageRating)} size="md" />
            </div>
            <div className="text-xs text-stone-500 mt-1.5">{reviews.length} değerlendirme</div>
          </div>
        </div>

        {/* Distribution bars */}
        <div className="md:col-span-3 space-y-1.5 flex flex-col justify-center">
          {ratingDist.map(({ star, count, pct }) => (
            <div key={star} className="flex items-center gap-2.5">
              <div className="flex items-center gap-1 w-8 flex-shrink-0">
                <span className="text-xs text-stone-500 font-medium">{star}</span>
                <i className="ri-star-fill text-amber-400 text-xs" />
              </div>
              <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    star >= 4 ? "bg-emerald-500" : star === 3 ? "bg-amber-400" : "bg-red-400"
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-stone-400 w-6 text-right flex-shrink-0">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Write review CTA */}
      {!alreadyReviewed && !showForm && (
        <div className={`mb-8 rounded-2xl border-2 border-dashed p-5 flex items-center gap-4 ${
          !user ? "border-stone-200 bg-stone-50" : "border-emerald-200 bg-emerald-50/40"
        }`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            !user ? "bg-stone-200" : "bg-emerald-100"
          }`}>
            <i className={`${!user ? "ri-user-line text-stone-400" : "ri-star-line text-emerald-600"} text-lg`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-stone-800 text-sm">
              {!user ? "Yorum yapmak için giriş yapın" : `${venueName} hakkında ne düşünüyorsunuz?`}
            </p>
            <p className="text-stone-500 text-xs mt-0.5">
              {!user ? "Gerçek deneyimleri paylaşmak topluluğa katkı sağlar." : "Mekanda olduğunuzda konum doğrulaması ile yorum yapabilirsiniz."}
            </p>
          </div>
          {!user ? (
            <Link
              to="/account"
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
            >
              <i className="ri-login-box-line" />
              Giriş Yap
            </Link>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
            >
              <i className="ri-edit-line" />
              Yorum Yaz
            </button>
          )}
        </div>
      )}

      {/* Review form */}
      {showForm && user && !alreadyReviewed && (
        <div className="mb-8 border border-stone-200 rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.displayName} className="w-9 h-9 rounded-full object-cover object-top" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{user.displayName.charAt(0).toUpperCase()}</span>
                </div>
              )}
              <div>
                <p className="font-semibold text-stone-900 text-sm">{user.displayName}</p>
                <p className="text-xs text-stone-400">@{user.username}</p>
              </div>
            </div>
            <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100 cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>

          {/* Location gate */}
          {!isNearVenue && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-2-line text-amber-600 text-lg" />
                </div>
                <div className="flex-1">
                  <p className="text-amber-800 font-semibold text-sm mb-1">Konum Doğrulaması Gerekli</p>
                  <p className="text-amber-700 text-xs leading-relaxed mb-3">
                    Gerçek ziyaretçileri doğrulamak için mekana 100m yakında olmanız gerekiyor.
                  </p>
                  {geo.status === "idle" && (
                    <button onClick={geo.requestLocation} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-crosshair-2-line" /> Konumumu Doğrula
                    </button>
                  )}
                  {geo.status === "loading" && (
                    <div className="flex items-center gap-2 text-amber-700 text-xs">
                      <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                      Konum alınıyor...
                    </div>
                  )}
                  {geo.status === "error" && <p className="text-red-600 text-xs">{geo.error}</p>}
                  {geo.status === "success" && !isNearVenue && (
                    <p className="text-amber-700 text-xs">Mekana yeterince yakın değilsiniz.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isNearVenue && (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-stone-700 mb-2">Puanınız *</label>
                <StarRating value={rating} onChange={setRating} size="lg" />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-stone-700 mb-2">Yorumunuz *</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Deneyiminizi paylaşın..."
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-emerald-400 resize-none"
                />
                <div className="text-right text-xs text-stone-400 mt-1">{comment.length}/500</div>
              </div>
              {submitState === "error" && (
                <div className="mb-4 px-4 py-3 rounded-xl text-sm bg-red-50 text-red-600 border border-red-200 flex items-center gap-2">
                  <i className="ri-error-warning-line flex-shrink-0" />{submitMsg}
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50 cursor-pointer transition-colors whitespace-nowrap">İptal</button>
                <button
                  type="submit"
                  disabled={submitState === "submitting"}
                  className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center gap-2"
                >
                  {submitState === "submitting" ? (
                    <><i className="ri-loader-4-line animate-spin" />Gönderiliyor...</>
                  ) : (
                    <><i className="ri-send-plane-line" />Gönder</>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Already reviewed success */}
      {alreadyReviewed && (
        <div className="mb-6 px-5 py-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
          <i className="ri-check-double-line text-emerald-600 text-lg" />
          <p className="text-emerald-700 text-sm font-medium">Bu mekan için yorumunuz mevcut.</p>
        </div>
      )}

      {/* Success toast after submit */}
      {submitState === "success" && (
        <div className="mb-6 px-5 py-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
          <i className="ri-check-double-line text-emerald-600 text-lg" />
          <p className="text-emerald-700 text-sm">{submitMsg}</p>
        </div>
      )}

      {/* Sort */}
      {reviews.length > 0 && (
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-stone-500">
            <span className="font-semibold text-stone-800">{reviews.length}</span> yorum
          </p>
          <div className="flex items-center gap-1 p-1 bg-stone-100 rounded-xl">
            {(["recent", "helpful", "rating"] as const).map((opt) => (
              <button
                key={opt}
                onClick={() => setSortBy(opt)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  sortBy === opt ? "bg-white text-stone-800 shadow-sm" : "text-stone-500 hover:text-stone-700"
                }`}
              >
                {opt === "recent" ? "En Yeni" : opt === "helpful" ? "En Faydalı" : "En Yüksek"}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Review list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-stone-100 rounded-2xl h-28 animate-pulse" />
          ))}
        </div>
      ) : sortedReviews.length > 0 ? (
        <div className="space-y-3">
          {sortedReviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              voted={myVotes.has(review.id)}
              loggedIn={!!user}
              onToggleHelpful={toggleHelpful}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-14 border-2 border-dashed border-stone-200 rounded-2xl">
          <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-chat-3-line text-stone-400 text-2xl" />
          </div>
          <p className="text-stone-600 font-semibold text-sm mb-1">Henüz yorum yok</p>
          <p className="text-stone-400 text-xs">{venueName} için ilk yorumu siz yapın!</p>
        </div>
      )}
    </section>
  );
}
